import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { PrimaryButton } from '../components/premium';
import { api, ApiError } from '../lib/api';
import { computeNutritionTargets } from '../lib/profileCalculations';
import { ftInToCm, lbsToKg } from '../lib/units';
import { setTrainingPrefs, WEEKDAY_LABELS_SHORT, DEFAULT_TRAINING_PREFS } from '../lib/trainingPrefs';
import { TRACKING_MODULE_LABELS, type TrackingModule } from '../lib/trackingPrefs';
import { isNativeApp, isNativeIOS } from '../lib/platform';
import { ensureAppleHealthAuth, isAppleHealthAuthorized, syncAppleHealth } from '../lib/appleHealth';

type Step =
  | 'welcome'
  | 'goal'
  | 'body'
  | 'activity'
  | 'schedule'
  | 'targets'
  | 'tracking'
  | 'appleHealth'
  | 'done';

export function OnboardingPage() {
  const nav = useNavigate();
  const { user, refresh, setUserFields } = useAuth();
  const steps = useMemo<Step[]>(() => {
    const core: Step[] = ['welcome', 'goal', 'body', 'activity', 'schedule', 'targets', 'tracking'];
    if (isNativeIOS()) core.push('appleHealth');
    core.push('done');
    return core;
  }, []);
  const [step, setStep] = useState<Step>('welcome');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [age, setAge] = useState('28');
  const [sex, setSex] = useState('male');
  const [heightCmStr, setHeightCmStr] = useState('175');
  const [heightFt, setHeightFt] = useState('5');
  const [heightIn, setHeightIn] = useState('9');
  const [weightKgStr, setWeightKgStr] = useState('75');
  const [weightLbsStr, setWeightLbsStr] = useState('165');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [fitnessGoal, setFitnessGoal] = useState<'cut' | 'bulk' | 'maintenance'>('maintenance');
  const [trackNutrition, setTrackNutrition] = useState(true);
  const [trackWorkouts, setTrackWorkouts] = useState(true);
  const [trackSleep, setTrackSleep] = useState(true);
  const [trackGoals, setTrackGoals] = useState(true);
  const [circlesEnabled, setCirclesEnabled] = useState(true);
  const [leaderboardsOptIn, setLeaderboardsOptIn] = useState(false);
  const [targetsEdited, setTargetsEdited] = useState(false);
  const [calorieStr, setCalorieStr] = useState('');
  const [proteinStr, setProteinStr] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);
  const [healthConnected, setHealthConnected] = useState(() => isAppleHealthAuthorized());
  const [daysPerWeek, setDaysPerWeek] = useState<number>(DEFAULT_TRAINING_PREFS.daysPerWeek ?? 4);
  const [preferredDays, setPreferredDays] = useState<number[]>(DEFAULT_TRAINING_PREFS.preferredWorkoutDays);

  const idx = Math.max(0, steps.indexOf(step));

  function metrics() {
    const h =
      unitSystem === 'metric'
        ? Math.max(50, Number(heightCmStr) || 170)
        : ftInToCm(Number(heightFt) || 5, Number(heightIn) || 9);
    const w =
      unitSystem === 'metric'
        ? Math.max(30, Number(weightKgStr) || 70)
        : lbsToKg(Math.max(66, Number(weightLbsStr) || 150));
    return { heightCm: h, weightKg: w };
  }

  const computedTargets = useMemo(() => {
    const { heightCm, weightKg } = metrics();
    return computeNutritionTargets({
      weightKg,
      heightCm,
      age: Math.max(14, Math.min(99, Number(age) || 30)),
      sex,
      activityLevel,
      fitnessGoal,
    });
  }, [age, sex, activityLevel, fitnessGoal, heightCmStr, heightFt, heightIn, weightKgStr, weightLbsStr, unitSystem]);

  const displayCalories = targetsEdited ? calorieStr : String(computedTargets.calorieTarget);
  const displayProtein = targetsEdited ? proteinStr : String(computedTargets.proteinTargetG);

  async function finish() {
    setErr(null);
    setBusy(true);
    try {
      const { heightCm, weightKg } = metrics();
      await api('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({
          displayName: displayName.trim() || null,
          preferredUnitSystem: unitSystem,
          onboardingCompleted: true,
          trackNutrition,
          trackWorkouts,
          trackSleep,
          trackGoals,
          circlesEnabled,
          leaderboardsOptIn,
        }),
      });
      await api('/api/profile', {
        method: 'PUT',
        body: JSON.stringify({
          age: Math.max(14, Math.min(99, Number(age) || 30)),
          sex,
          heightCm,
          weightKg,
          activityLevel,
          fitnessGoal,
          ...(targetsEdited
            ? {
                manualNutritionTargets: true,
                calorieTarget: Number(displayCalories) || computedTargets.calorieTarget,
                proteinTargetG: Number(displayProtein) || computedTargets.proteinTargetG,
              }
            : {}),
        }),
      });
      setUserFields({
        displayName: displayName.trim() || null,
        preferredUnitSystem: unitSystem,
        onboardingCompleted: true,
        trackNutrition,
        trackWorkouts,
        trackSleep,
        trackGoals,
        circlesEnabled,
        leaderboardsOptIn,
      });
      await refresh();
      setTrainingPrefs(user?.id, { daysPerWeek, preferredWorkoutDays: preferredDays });
      if (isNativeApp()) {
        nav('/', { replace: true });
      } else {
        nav('/checkout', { replace: true });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        try {
          const j = JSON.parse(e.body ?? '{}') as { error?: string };
          setErr(j.error ?? 'Something went wrong.');
        } catch {
          setErr('Something went wrong.');
        }
      } else setErr('Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  function next() {
    setErr(null);
    if (idx < steps.length - 1) setStep(steps[idx + 1]!);
  }
  function back() {
    if (idx > 0) setStep(steps[idx - 1]!);
  }

  return (
    <div className="app-body relative min-h-screen px-4 pb-8 pt-[max(1.5rem,env(safe-area-inset-top))]">
      <div className="mx-auto w-full min-w-0 max-w-lg">
        <div className="flex items-center justify-center gap-1.5">
          {steps.map((s, i) => (
            <span
              key={s}
              className={`h-1.5 rounded-full transition-all ${
                i <= idx ? 'w-6 bg-accent' : 'w-1.5 bg-white/15'
              }`}
            />
          ))}
        </div>
        <p className="mt-3 text-center text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
          Step {idx + 1} of {steps.length}
        </p>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            className="premium-card mt-6 !p-6 md:!p-8"
          >            {step === 'welcome' && (
              <>
                <div className="mb-5 flex justify-center">
                  <BrandLogo variant="hero" />
                </div>
                <h1 className="font-display text-xl font-bold text-[var(--text-primary)] sm:text-2xl">
                  Welcome{user?.displayName ? `, ${user.displayName}` : ''}
                </h1>
                <p className="mt-2 text-sm text-[var(--text-muted)]">Let&apos;s build your personalized plan.</p>                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setUnitSystem('metric')}
                    className={`onboarding-pick px-4 py-3 text-left text-sm transition ${
                      unitSystem === 'metric' ? 'is-selected' : 'is-unselected'
                    }`}
                  >
                    Metric (kg, cm)
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnitSystem('imperial')}
                    className={`onboarding-pick px-4 py-3 text-left text-sm transition ${
                      unitSystem === 'imperial' ? 'is-selected' : 'is-unselected'
                    }`}
                  >
                    US (lb, ft/in)
                  </button>
                </div>
              </>
            )}
            {step === 'goal' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">What&apos;s your goal?</h2>
                <div className="mt-4 space-y-2">
                  {(
                    [
                      ['cut', 'Cut', 'Lose fat, keep strength'],
                      ['maintenance', 'Maintain', 'Stay lean and steady'],
                      ['bulk', 'Build muscle', 'Grow with control'],
                    ] as const
                  ).map(([g, title, sub]) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setFitnessGoal(g)}
                      className={`onboarding-pick w-full px-4 py-3 text-left transition ${
                        fitnessGoal === g ? 'is-selected' : 'is-unselected'
                      }`}
                    >
                      <span className="block text-sm font-semibold">{title}</span>
                      <span className="mt-0.5 block text-xs opacity-70">{sub}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {step === 'body' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">About your body</h2>
                <label className="mt-4 block text-xs text-[var(--text-muted)]">
                  Name
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-[var(--text-primary)] outline-none"
                  />
                </label>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="text-xs text-[var(--text-muted)]">
                    Age
                    <input
                      type="number"
                      min={14}
                      max={99}
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-[var(--text-primary)] outline-none"
                    />
                  </label>
                  <label className="text-xs text-[var(--text-muted)]">
                    Sex
                    <select
                      value={sex}
                      onChange={(e) => setSex(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-[var(--text-primary)] outline-none"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setUnitSystem('metric')}
                    className={`onboarding-pick px-4 py-3 text-left text-sm transition ${
                      unitSystem === 'metric' ? 'is-selected' : 'is-unselected'
                    }`}
                  >
                    Metric (kg, cm)
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnitSystem('imperial')}
                    className={`onboarding-pick px-4 py-3 text-left text-sm transition ${
                      unitSystem === 'imperial' ? 'is-selected' : 'is-unselected'
                    }`}
                  >
                    US (lb, ft/in)
                  </button>
                </div>                {unitSystem === 'metric' ? (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label className="text-xs text-zinc-500">
                      Height (cm)
                      <input
                        type="number"
                        value={heightCmStr}
                        onChange={(e) => setHeightCmStr(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900/80 px-4 py-3 text-sm text-white"
                      />
                    </label>
                    <label className="text-xs text-zinc-500">
                      Weight (kg)
                      <input
                        type="number"
                        value={weightKgStr}
                        onChange={(e) => setWeightKgStr(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900/80 px-4 py-3 text-sm text-white"
                      />
                    </label>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs text-zinc-500">
                        Ft
                        <input
                          type="number"
                          value={heightFt}
                          onChange={(e) => setHeightFt(e.target.value)}
                          className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900/80 px-4 py-3 text-sm text-white"
                        />
                      </label>
                      <label className="text-xs text-zinc-500">
                        In
                        <input
                          type="number"
                          value={heightIn}
                          onChange={(e) => setHeightIn(e.target.value)}
                          className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900/80 px-4 py-3 text-sm text-white"
                        />
                      </label>
                    </div>
                    <label className="text-xs text-zinc-500">
                      Weight (lb)
                      <input
                        type="number"
                        value={weightLbsStr}
                        onChange={(e) => setWeightLbsStr(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900/80 px-4 py-3 text-sm text-white"
                      />
                    </label>
                  </div>
                )}
              </>
            )}
            {step === 'activity' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Activity level</h2>                <div className="mt-4 space-y-2">
                  {(['sedentary', 'light', 'moderate', 'active', 'very_active'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setActivityLevel(v)}
                      className={`onboarding-pick w-full px-4 py-3 text-left text-sm capitalize transition ${
                        activityLevel === v ? 'is-selected' : 'is-unselected'
                      }`}
                    >
                      {v.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </>
            )}
            {step === 'schedule' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Training schedule</h2>
                <p className="mt-2 text-sm text-[var(--text-muted)]">How many days per week do you train?</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[2, 3, 4, 5, 6, 7].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setDaysPerWeek(n)}
                      className={`onboarding-pick px-4 py-2 text-sm font-semibold ${
                        daysPerWeek === n ? 'is-selected' : 'is-unselected'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="mt-5 text-sm text-[var(--text-muted)]">Preferred workout days</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {WEEKDAY_LABELS_SHORT.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() =>
                        setPreferredDays((prev) =>
                          prev.includes(i) ? prev.filter((d) => d !== i) : [...prev, i].sort(),
                        )
                      }
                      className={`onboarding-pick px-3 py-2 text-xs font-semibold ${
                        preferredDays.includes(i) ? 'is-selected' : 'is-unselected'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
            {step === 'targets' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Your targets</h2>
                <p className="mt-2 text-sm text-[var(--text-muted)]">Generated from your stats — edit if needed.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="text-xs text-[var(--text-muted)]">
                    Daily calories
                    <input
                      type="number"
                      value={displayCalories}
                      onChange={(e) => {
                        setTargetsEdited(true);
                        setCalorieStr(e.target.value);
                      }}
                      className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-[var(--text-primary)] outline-none"
                    />
                  </label>
                  <label className="text-xs text-[var(--text-muted)]">
                    Protein (g)
                    <input
                      type="number"
                      value={displayProtein}
                      onChange={(e) => {
                        setTargetsEdited(true);
                        setProteinStr(e.target.value);
                      }}
                      className="mt-1 w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-[var(--text-primary)] outline-none"
                    />
                  </label>
                </div>
                <p className="mt-3 text-xs text-[var(--text-muted)]">
                  Maintenance ~{computedTargets.maintenanceCalories} kcal
                </p>
              </>
            )}            {step === 'tracking' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">What to track</h2>                <p className="mt-2 text-sm text-zinc-400">
                  Turn off anything you don&apos;t need. You can change this later in Profile.
                </p>
                <div className="mt-4 space-y-3">
                  {(
                    [
                      ['nutrition', trackNutrition, setTrackNutrition],
                      ['workouts', trackWorkouts, setTrackWorkouts],
                      ['sleep', trackSleep, setTrackSleep],
                      ['goals', trackGoals, setTrackGoals],
                    ] as const
                  ).map(([key, on, setOn]) => {
                    const meta = TRACKING_MODULE_LABELS[key as TrackingModule];
                    return (
                      <label
                        key={key}
                        className="flex cursor-pointer items-start gap-3 rounded-2xl border border-white/10 bg-zinc-900/50 px-4 py-3"
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => setOn(e.target.checked)}
                          className="mt-1 rounded border-white/20 bg-zinc-900"
                        />
                        <span>
                          <span className="block text-sm font-medium text-white">{meta.title}</span>
                          <span className="mt-0.5 block text-xs text-zinc-500">{meta.description}</span>
                        </span>
                      </label>
                    );
                  })}
                  <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-violet-500/20 bg-violet-500/5 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={circlesEnabled}
                      onChange={(e) => setCirclesEnabled(e.target.checked)}
                      className="mt-1 rounded border-white/20 bg-zinc-900"
                    />
                    <span>
                      <span className="block text-sm font-medium text-white">Circles</span>
                      <span className="mt-0.5 block text-xs text-zinc-500">
                        Private groups to stay accountable with friends — change anytime in Profile
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={leaderboardsOptIn}
                      onChange={(e) => setLeaderboardsOptIn(e.target.checked)}
                      className="mt-1 rounded border-white/20 bg-zinc-900"
                    />
                    <span>
                      <span className="block text-sm font-medium text-white">Leaderboards</span>
                      <span className="mt-0.5 block text-xs text-zinc-500">
                        Show your momentum score on individual and circle leaderboards — optional
                      </span>
                    </span>
                  </label>
                </div>
              </>
            )}
            {step === 'appleHealth' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Apple Health</h2>
                <p className="mt-2 text-sm text-[var(--text-muted)]">
                  Sync steps, active calories, workouts, and weight from Apple Health so your dashboard stays accurate.
                  You can change this later in Profile.
                </p>
                {healthConnected ? (
                  <div className="mt-5 rounded-2xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                    Synced with Apple Health
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={healthBusy}
                    onClick={() => {
                      void (async () => {
                        setHealthBusy(true);
                        setErr(null);
                        try {
                          await ensureAppleHealthAuth();
                          await syncAppleHealth({ force: true });
                          setHealthConnected(true);
                        } catch (e) {
                          setErr(e instanceof Error ? e.message : 'Could not connect Apple Health.');
                        } finally {
                          setHealthBusy(false);
                        }
                      })();
                    }}
                    className="mt-5 w-full rounded-2xl bg-[#4F8CFF] px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {healthBusy ? 'Connecting…' : 'Connect Apple Health'}
                  </button>
                )}
                <p className="mt-3 text-xs text-[var(--text-muted)]">
                  Optional — tap Continue to skip for now.
                </p>
              </>
            )}
            {step === 'done' && (
              <>
                <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">Your plan is ready</h2>
                <p className="mt-3 text-sm text-[var(--text-muted)]">Enter the app and start logging.</p>              </>
            )}
            {err ? <p className="mt-4 text-sm text-red-300">{err}</p> : null}
            <div className="mt-8 flex flex-wrap gap-3">
              {idx > 0 && step !== 'done' ? (
                <button
                  type="button"
                  onClick={back}
                  className="rounded-2xl border border-white/15 px-5 py-2.5 text-sm text-zinc-300"
                >
                  Back
                </button>
              ) : null}
              <div className="flex-1" />
              {step !== 'done' ? (
                <PrimaryButton onClick={next} className="sm:min-w-[140px]">
                  Continue
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={() => void finish()} disabled={busy} className="sm:min-w-[140px]">
                  {busy ? 'Saving…' : 'Enter app'}
                </PrimaryButton>
              )}            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
